import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';
import { TableSortableProvider } from './TableSortable.context';

/**
 * `Table.Sortable` is an HOC that can be used to create a `Table` with sortable columns.
 *
 * `Table.Sortable` is essentially a controlled component but does offer some uncontrolled
 * functionality. When a user clicks on a `Table.SortableHeaderCell`,
 * `Table.Sortable` determines which arrow icons to display across all column headers.
 * However, `Table.Sortable` does not actually sort the table data. Sorting is handled
 * by your own functions or API calls.
 *
 * @subcomponents ['Table.SortableHeaderCell']
 * @since 8.41.0
 */
export var TableSortable = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var _useState = useState(props.defaultSortDirections),
    _useState2 = _slicedToArray(_useState, 2),
    directions = _useState2[0],
    setDirections = _useState2[1];
  return /*#__PURE__*/React.createElement(TableSortableProvider, _extends({
    value: {
      directions: directions,
      setDirections: setDirections,
      onSortChange: props.onSortChange
    },
    ref: forwardedRef
  }, props));
});
TableSortable.displayName = 'Table.Sortable';
TableSortable.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Callback passed to every sortable header cell. Fired on click or keypress.
   * A `Table.SortableHeaderCell`
   * can also use their own `onSortChange` callback to override.
   *
   * Takes two arguments: an object `{currentSortDirection, nextSortDirection}` and `props`
   */
  onSortChange: PropTypes.func,
  /**
   * Sets the initial sort direction for each column.
   * Ex: `['ascending', 'none', 'none']`.
   *
   * Use `null` for non-sortable columns.
   */
  defaultSortDirections: PropTypes.arrayOf(PropTypes.string).isRequired
} : {};
TableSortable.defaultProps = {
  onSortChange: function onSortChange() {}
};